import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../Icon';
import Eyebrow from '../page/Eyebrow';
import { tie } from '../page/typography';
import ArticleMeta from './ArticleMeta';
import KeyDates from './KeyDates';
import { articleSections } from './sections';

/**
 * Стаття в блозі — великою карткою-анонсом.
 *
 * Поки стаття одна, тонка картка-рядок на всю ширину робила сторінку
 * порожньою: банер, абзац, рядок — і одразу підвал. Тут анонс показує, що
 * всередині: ключові дати (якщо стаття про строки — саме їх шукають
 * першими) і розділи з переходом просто в потрібний. Хто прийшов із
 * питанням «що робити, якщо не подали», не мусить гортати вступ і перелік
 * змін.
 *
 * Посилання на статтю — на заголовку, і ::after розтягує його на всю
 * картку; переходи в розділи лежать над ним (relative z-10), тож кожне
 * натискається окремо. Вкладати посилання одне в одне HTML не дозволяє.
 */
const FeaturedArticle = ({ article, headingAs: Heading = 'h2' }) => {
  const to = `/blog/${article.slug}`;
  const sections = articleSections(article);
  const datesId = `${article.slug}-daty`;
  return (
    <article className="group relative h-full flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm hover:border-teal-300 hover:shadow-md transition-[border-color,box-shadow] duration-300 overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-teal-600 to-sky-500" aria-hidden="true"></div>
      <div className="flex-1 flex flex-col p-6 sm:p-8 lg:p-10">
        <ArticleMeta article={article} />

        <Heading className="mt-5 font-display text-2xl/[1.2] sm:text-3xl/[1.2] lg:text-[2rem]/[1.2] font-extrabold text-slate-900 text-balance">
          <Link
            to={to}
            className="group-hover:text-teal-800 transition-colors duration-200 after:absolute after:inset-0 after:rounded-2xl focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-teal-700 focus-visible:after:ring-offset-2"
          >
            {tie(article.title)}
          </Link>
        </Heading>
        <p className="mt-4 max-w-2xl text-base lg:text-lg leading-relaxed text-slate-600">{tie(article.excerpt)}</p>

        {article.keyDates && article.keyDates.length > 0 && (
          <div className="mt-7 rounded-xl border border-teal-100 bg-teal-50/40 p-5">
            <Eyebrow id={datesId} className="mb-4">
              Ключові дати
            </Eyebrow>
            <KeyDates dates={article.keyDates} labelledBy={datesId} />
          </div>
        )}

        <div className="relative z-10 mt-7 pt-6 border-t border-slate-200">
          <Eyebrow className="mb-3">У статті</Eyebrow>
          <ul className="list-none m-0 p-0 grid gap-x-6 sm:grid-cols-2">
            {sections.map((section) => (
              <li key={section.id}>
                <Link
                  to={`${to}#${section.id}`}
                  className="group/link flex items-start gap-3 min-h-[2.75rem] py-2.5 text-[0.95rem] font-medium leading-snug text-slate-800 hover:text-teal-800 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 rounded-md"
                >
                  <Icon
                    name="fa-arrow-right"
                    className="mt-[0.3rem] flex-shrink-0 text-[0.7rem] text-teal-700 transition-transform duration-200 motion-reduce:transition-none group-hover/link:translate-x-0.5"
                  />
                  {tie(section.title)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <span
          className="mt-auto pt-8 inline-flex items-center gap-2 self-start font-semibold text-teal-700"
          aria-hidden="true"
        >
          Читати статтю
          <Icon name="fa-arrow-right" className="transition-transform duration-200 motion-reduce:transition-none group-hover:translate-x-1" />
        </span>
      </div>
    </article>
  );
};

export default FeaturedArticle;
